import React from 'react'
import { Icon } from './icons'

// ─── Button ─────────────────────────────────────────────────────────
export interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  icon?: string | null
  iconRight?: string | null
  children?: React.ReactNode
  disabled?: boolean
  fullWidth?: boolean
  style?: React.CSSProperties
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement>
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement>
  type?: 'button' | 'submit' | 'reset'
  title?: string
  'aria-label'?: string
  'aria-pressed'?: boolean
  className?: string
}

export function Button({
  variant = 'primary', size = 'md', icon = null, iconRight = null,
  children, disabled = false, fullWidth = false, style = {},
  onClick, onMouseEnter, onMouseLeave, type = 'button', title,
  'aria-label': ariaLabel, 'aria-pressed': ariaPressed, className,
}: ButtonProps) {
  const sizing = {
    sm: { h: 28, px: 10, gap: 6, fs: 12.5, ic: 14, sw: 1.8 },
    md: { h: 36, px: 14, gap: 8, fs: 13.5, ic: 16, sw: 1.7 },
    lg: { h: 48, px: 18, gap: 10, fs: 15,   ic: 18, sw: 1.7 },
  }[size]

  const variantStyle: React.CSSProperties = {
    primary:   { background: 'var(--accent)',              color: 'var(--accent-ink)',    border: '1px solid var(--accent)' },
    secondary: { background: 'var(--bg-surface-2)',        color: 'var(--text)',          border: '1px solid var(--border-strong)' },
    ghost:     { background: 'transparent',                color: 'var(--text)',          border: '1px solid var(--border)' },
    danger:    { background: 'var(--status-blocked-bg)',   color: 'var(--status-blocked-tx)', border: '1px solid var(--status-blocked-dot)' },
  }[variant] as React.CSSProperties

  return (
    <button
      type={type} title={title} aria-label={ariaLabel} aria-pressed={ariaPressed}
      onClick={onClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
      disabled={disabled} className={className}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        gap: sizing.gap, height: sizing.h, padding: `0 ${sizing.px}px`,
        width: fullWidth ? '100%' : 'auto',
        borderRadius: 'var(--r-md)',
        ...variantStyle,
        fontFamily: 'var(--font-sans)', fontSize: sizing.fs, fontWeight: 600, letterSpacing: 0.15,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        whiteSpace: 'nowrap', lineHeight: 1,
        transition: 'background var(--dur-fast) var(--ease-out), filter var(--dur-fast) var(--ease-out)',
        ...style,
      }}>
      {icon && <Icon name={icon} size={sizing.ic} sw={sizing.sw}/>}
      {children}
      {iconRight && <Icon name={iconRight} size={sizing.ic} sw={sizing.sw}/>}
    </button>
  )
}

// ─── StatusPill ─────────────────────────────────────────────────────
export type StatusValue = 'not_started' | 'in_progress' | 'blocked' | 'completed' | 'on_hold' | 'cancelled'

export const STATUS_LABEL: Record<StatusValue, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  blocked:     'Blocked',
  completed:   'Completed',
  on_hold:     'On hold',
  cancelled:   'Cancelled',
}

export interface StatusPillProps {
  status?: StatusValue
  size?: 'sm' | 'md'
  style?: React.CSSProperties
}

export function StatusPill({ status = 'not_started', size = 'sm', style = {} }: StatusPillProps) {
  const key = status.replace(/_/g, '-')
  const sizing = size === 'sm' ? { py: 3, px: 8, fs: 11 } : { py: 5, px: 10, fs: 12.5 }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: `${sizing.py}px ${sizing.px}px`, borderRadius: 'var(--r-sm)',
      background: `var(--status-${key}-bg)`,
      color:      `var(--status-${key}-tx)`,
      fontFamily: 'var(--font-sans)', fontSize: sizing.fs, fontWeight: 500,
      lineHeight: 1, letterSpacing: 0.1, whiteSpace: 'nowrap',
      ...style,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: `var(--status-${key}-dot)` }}/>
      {STATUS_LABEL[status]}
    </span>
  )
}

// ─── PriorityTag ────────────────────────────────────────────────────
export type PriorityValue = 'urgent' | 'high' | 'normal' | 'low'

export interface PriorityTagProps {
  p?: PriorityValue
  style?: React.CSSProperties
}

export function PriorityTag({ p = 'normal', style = {} }: PriorityTagProps) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 500,
      color: `var(--priority-${p}-tx)`,
      letterSpacing: 0.2, textTransform: 'capitalize', lineHeight: 1,
      ...style,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: `var(--priority-${p}-dot)` }}/>
      {p}
    </span>
  )
}

// ─── Avatar ─────────────────────────────────────────────────────────
const KNOWN_IDS = new Set(['JM', 'MC', 'AM', 'RT', 'TG', 'DM', 'UN'])

export interface AvatarProps {
  id?: string
  src?: string | null
  alt?: string | null
  size?: number
  ring?: string | null
  style?: React.CSSProperties
}

export function Avatar({ id = 'UN', src = null, alt = null, size = 24, ring = null, style = {} }: AvatarProps) {
  const [imgFailed, setImgFailed] = React.useState(false)
  const showPhoto = src && !imgFailed
  const known = KNOWN_IDS.has(id) ? id : 'UN'
  const shared: React.CSSProperties = {
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    boxShadow: ring ? `0 0 0 2px ${ring}` : 'none',
    ...style,
  }
  if (showPhoto) {
    return (
      <img src={src} alt={alt || id} onError={() => setImgFailed(true)}
        style={{ ...shared, objectFit: 'cover', display: 'block' }}/>
    )
  }
  return (
    <div role="img" aria-label={alt || id} style={{
      ...shared,
      background: `var(--avatar-${known}-bg)`,
      color:      `var(--avatar-${known}-ink)`,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-sans)', fontSize: size * 0.42, fontWeight: 700, letterSpacing: 0.3,
    }}>{id}</div>
  )
}

// ─── AvatarStack ────────────────────────────────────────────────────
export interface AvatarEntry { id: string; src?: string }

export interface AvatarStackProps {
  ids?: Array<string | AvatarEntry>
  size?: number
  max?: number
  style?: React.CSSProperties
}

export function AvatarStack({ ids = [], size = 22, max = 3, style = {} }: AvatarStackProps) {
  const show = ids.slice(0, max)
  const rest = ids.length - show.length
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', ...style }}>
      {show.map((entry, i) => {
        const obj: AvatarEntry = typeof entry === 'string' ? { id: entry } : entry
        return (
          <div key={i} style={{ marginLeft: i ? -8 : 0 }}>
            <Avatar id={obj.id} src={obj.src} size={size} ring="var(--bg-surface)"/>
          </div>
        )
      })}
      {rest > 0 && (
        <div style={{
          marginLeft: -8, width: size, height: size, borderRadius: '50%',
          background: 'var(--bg-elevated)', color: 'var(--text-muted)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--font-sans)', fontSize: size * 0.4, fontWeight: 600,
          boxShadow: '0 0 0 2px var(--bg-surface)',
        }}>+{rest}</div>
      )}
    </div>
  )
}

// ─── ProjectDot ─────────────────────────────────────────────────────
export interface ProjectDotProps {
  color?: string
  size?: number
  style?: React.CSSProperties
}

export function ProjectDot({ color = 'var(--proj-cedar)', size = 8, style = {} }: ProjectDotProps) {
  return <span style={{
    width: size, height: size, borderRadius: '50%', background: color,
    display: 'inline-block', flexShrink: 0, ...style,
  }}/>
}

// ─── TypeTag ─────────────────────────────────────────────────────────
export interface TypeTagProps {
  children?: React.ReactNode
  style?: React.CSSProperties
}

export function TypeTag({ children, style = {} }: TypeTagProps) {
  return (
    <span style={{
      fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 500,
      letterSpacing: 0.5, textTransform: 'uppercase',
      color: 'var(--text-muted)', background: 'var(--border-muted)',
      padding: '3px 7px', borderRadius: 'var(--r-xs)', whiteSpace: 'nowrap',
      ...style,
    }}>{children}</span>
  )
}

// ─── DeltaPill ───────────────────────────────────────────────────────
export interface DeltaPillProps {
  value: number
  baseline?: string
  tone?: 'up' | 'down' | 'flat'
  style?: React.CSSProperties
}

export function DeltaPill({ value, baseline = 'vs last month', tone, style = {} }: DeltaPillProps) {
  const explicit = tone || (value > 0 ? 'up' : value < 0 ? 'down' : 'flat')
  const palette = {
    up:   { fg: 'var(--brand-gold)',    bg: 'var(--brand-gold-soft)' },
    down: { fg: 'var(--st-blocked)',    bg: 'var(--st-blocked-soft)' },
    flat: { fg: 'var(--text-muted)',    bg: 'transparent' },
  }[explicit]
  const arrow = explicit === 'up' ? 'M2 8L6 4L10 8'
              : explicit === 'down' ? 'M2 4L6 8L10 4'
              : 'M2 6h8'
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600,
      color: palette.fg, background: palette.bg,
      padding: '4px 8px', borderRadius: 'var(--r-sm)',
      whiteSpace: 'nowrap', lineHeight: 1,
      ...style,
    }}>
      <svg width="9" height="9" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
        <path d={arrow} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      {value > 0 ? '+' : ''}{value}%
      {baseline && (
        <span style={{ color: 'var(--text-dim)', fontWeight: 500, marginLeft: 2 }}>{baseline}</span>
      )}
    </span>
  )
}

// ─── Segmented ───────────────────────────────────────────────────────
export interface SegmentedOption { id: string; label: string }

export interface SegmentedProps {
  label?: string | null
  value: string
  onChange?: (id: string) => void
  options?: SegmentedOption[]
  size?: 'sm' | 'md'
  style?: React.CSSProperties
}

export function Segmented({ label = null, value, onChange, options = [], size = 'sm', style = {} }: SegmentedProps) {
  const sizing = size === 'md' ? { h: 32, px: 14, fs: 13 } : { h: 28, px: 12, fs: 12 }
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...style }}>
      {label && (
        <span style={{
          fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 600,
          letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-muted)',
        }}>{label}</span>
      )}
      <div style={{
        display: 'inline-flex', padding: 2,
        background: 'var(--bg-elevated)', border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)', height: sizing.h,
      }}>
        {options.map(o => {
          const on = o.id === value
          return (
            <button key={o.id} onClick={() => onChange?.(o.id)} style={{
              border: 'none', cursor: 'pointer', padding: `0 ${sizing.px}px`,
              borderRadius: 'calc(var(--r-md) - 2px)',
              background: on ? 'var(--brand-gold)' : 'transparent',
              color: on ? 'var(--brand-gold-ink)' : 'var(--text-muted)',
              fontFamily: 'var(--font-sans)', fontSize: sizing.fs, fontWeight: 600,
              letterSpacing: 0.1, lineHeight: 1,
              transition: 'all var(--dur-fast) var(--ease-out)', whiteSpace: 'nowrap',
            }}>{o.label}</button>
          )
        })}
      </div>
    </div>
  )
}

// ─── SpecTypeBadge ────────────────────────────────────────────────────
export function SpecTypeBadge({ specType, style = {} }: { specType: string; style?: React.CSSProperties }) {
  if (!specType) return null
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 7px', borderRadius: 'var(--r-sm)',
      fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
      background: 'var(--bg-surface-2)', color: 'var(--text-muted)', border: '1px solid var(--border)',
      ...style,
    }}>
      {specType.replace(/_/g, ' ')}
    </span>
  )
}

// ─── KindBadge ───────────────────────────────────────────────────────
export function KindBadge({ kind, style = {} }: { kind: 'selection' | 'scope' | string; style?: React.CSSProperties }) {
  const isSel = kind === 'selection'
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 7px', borderRadius: 'var(--r-sm)',
      fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
      background: isSel ? 'var(--status-info-bg)' : 'var(--status-draft-bg)',
      color: isSel ? 'var(--status-info-tx)' : 'var(--status-draft-tx)',
      ...style,
    }}>
      {kind}
    </span>
  )
}

// ─── RoomRailItem ─────────────────────────────────────────────────────
export interface RoomRailItemProps {
  name: string
  count: number
  pending: number
  active?: boolean
  onClick?: () => void
  style?: React.CSSProperties
}
export function RoomRailItem({ name, count, pending, active = false, onClick, style = {} }: RoomRailItemProps) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      width: '100%', padding: '9px 14px',
      background: active ? 'var(--accent-light)' : 'transparent',
      border: 'none', borderRadius: 'var(--r-md)',
      cursor: 'pointer', textAlign: 'left',
      color: active ? 'var(--accent)' : 'var(--text)',
      fontWeight: active ? 600 : 400, fontSize: 13,
      transition: 'background var(--dur-fast) var(--ease-out)',
      ...style,
    }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{name}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 8 }}>
        {pending > 0 && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 9999, background: 'var(--status-pending-bg)', color: 'var(--status-pending-tx)' }}>{pending}</span>
        )}
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{count}</span>
      </span>
    </button>
  )
}

// ─── AllowanceDelta ───────────────────────────────────────────────────
export function AllowanceDelta({ allowance, selected, style = {} }: { allowance: number; selected?: number | null; style?: React.CSSProperties }) {
  if (!allowance && selected == null) return null
  const delta = (selected ?? 0) - allowance
  return (
    <div style={{ fontSize: 12, ...style }}>
      <span style={{ color: 'var(--text-muted)' }}>${allowance.toLocaleString()}</span>
      {selected != null && delta !== 0 && (
        <span style={{ marginLeft: 6, fontWeight: 600, color: delta > 0 ? 'var(--status-blocked-tx)' : 'var(--status-done-tx)' }}>
          {delta > 0 ? '+' : ''}${Math.abs(delta).toLocaleString()}
        </span>
      )}
    </div>
  )
}
